import React from 'react';

interface MarkdownTextProps {
    content: string;
}

/**
 * Renders rich text content. 
 * Now supports both HTML (from WYSIWYG) and basic Markdown for backward compatibility.
 */
export const MarkdownText: React.FC<MarkdownTextProps> = ({ content }) => {
    if (!content) return null;

    // Si le contenu semble être du HTML (contient des balises), on le rend directement
    const isHtml = /<[a-z][\s\S]*>/i.test(content);

    if (isHtml) {
        return (
            <>
                <div
                    className="wysiwyg-rendered-content"
                    dangerouslySetInnerHTML={{ __html: content }}
                />
                <style>{`
                    .wysiwyg-rendered-content {
                        overflow-wrap: break-word;
                        word-wrap: break-word;
                        word-break: break-word;
                        width: 100%;
                        max-width: 100%;
                        box-sizing: border-box;
                    }
                    .wysiwyg-rendered-content strong, 
                    .wysiwyg-rendered-content b { 
                        font-weight: 700 !important; 
                        display: inline;
                    }
                    .wysiwyg-rendered-content em, 
                    .wysiwyg-rendered-content i { 
                        font-style: italic !important; 
                        display: inline;
                    }
                    .wysiwyg-rendered-content p { 
                        margin-bottom: 0.5rem; 
                    }
                    .wysiwyg-rendered-content p:last-child { 
                        margin-bottom: 0; 
                    }
                    .wysiwyg-rendered-content ul { 
                        list-style-type: disc !important; 
                        padding-left: 1.5rem !important; 
                        margin-bottom: 0.5rem !important; 
                        display: block !important; 
                    }
                    .wysiwyg-rendered-content ol { 
                        list-style-type: decimal !important; 
                        padding-left: 1.5rem !important; 
                        margin-bottom: 0.5rem !important; 
                        display: block !important; 
                    }
                    .wysiwyg-rendered-content li { 
                        display: list-item !important; 
                        margin-bottom: 0.25rem !important; 
                    }
                `}</style>
            </>
        );
    }

    // Sinon, on garde l'ancien parseur Markdown pour la compatibilité
    const lines = content.split('\n');

    return (
        <div className="markdown-rendered-content">
            <style>{`
                .markdown-rendered-content {
                    overflow-wrap: break-word;
                    word-wrap: break-word;
                    word-break: break-word;
                    width: 100%;
                }
            `}</style>
            {lines.map((line, lineIdx) => {
                const isBullet = line.trim().startsWith('•') || line.trim().startsWith('* ');
                const cleanLine = isBullet ? line.replace(/^\s*(•|\*\s)/, '') : line;

                const parseInline = (text: string) => {
                    const parts = [];
                    let currentPos = 0;
                    const regex = /(\*\*.*?\*\*|\*.*?\*)/g;
                    let match;

                    while ((match = regex.exec(text)) !== null) {
                        const fullMatch = match[0];
                        const startIdx = match.index;
                        if (startIdx > currentPos) parts.push(text.substring(currentPos, startIdx));
                        if (fullMatch.startsWith('**')) {
                            parts.push(<strong key={startIdx}>{fullMatch.substring(2, fullMatch.length - 2)}</strong>);
                        } else if (fullMatch.startsWith('*')) {
                            parts.push(<em key={startIdx}>{fullMatch.substring(1, fullMatch.length - 1)}</em>);
                        }
                        currentPos = startIdx + fullMatch.length;
                    }
                    if (currentPos < text.length) parts.push(text.substring(currentPos));
                    return parts.length === 0 ? text : parts;
                };

                const contentElement = <span key={lineIdx}>{parseInline(cleanLine)}</span>;

                if (isBullet) {
                    return (
                        <div key={lineIdx} className="flex items-start gap-2 ml-2">
                            <span className="flex-shrink-0 mt-1">•</span>
                            <span className="flex-1">{contentElement}</span>
                        </div>
                    );
                }

                return <div key={lineIdx} className={line.trim() === '' ? 'h-2' : ''}>{contentElement}</div>;
            })}
        </div>
    );
};
